import type { Lod, VNode } from './canvasModel';
import type { Theme } from './theme';

export interface CardGeometry {
  w: number;
  h: number;
}

const IMG = 88;
const PAD = 14;
const FONT = 'Inter, system-ui, sans-serif';
/** Schriftgrößen und Zeilenhöhen: Titel, Zeile „Klasse · @schlüssel“, Hover-Text (auch auf Laptops gut lesbar) */
const TITLE = { size: 20, line: 24 } as const;
const META = 15;
const HOVER = { size: 15, line: 20 } as const;

/** Grobe Textbreite (ohne Messung, damit die Berechnung überall gleich und testbar ist). */
export const charWidth = (fontPx: number, bold: boolean): number => fontPx * (bold ? 0.6 : 0.54);

/** Bricht Text nach Wörtern um und kürzt mit „…“ nach `maxLines` Zeilen. */
export function wrapText(text: string, maxPx: number, fontPx: number, maxLines: number, bold = false): string[] {
  const perLine = Math.max(4, Math.floor(maxPx / charWidth(fontPx, bold)));
  const words = text.replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
  const lines: string[] = [];
  let line = '';
  for (const raw of words) {
    let word = raw;
    while (word.length > perLine) {
      // sehr lange Wörter hart trennen
      if (line) {
        lines.push(line);
        line = '';
      }
      lines.push(word.slice(0, perLine));
      word = word.slice(perLine);
    }
    const next = line ? `${line} ${word}` : word;
    if (next.length <= perLine) line = next;
    else {
      lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  if (lines.length <= maxLines) return lines;
  const kept = lines.slice(0, maxLines);
  const last = kept[maxLines - 1]!;
  kept[maxLines - 1] = `${last.slice(0, Math.max(1, perLine - 1)).trimEnd()}…`;
  return kept;
}

/** Größe der Karte je Zoomstufe (Stufe 0 und leere Nodes werden als Kreis ohne Karte gezeichnet). */
export function cardGeometry(v: VNode, lod: Lod): CardGeometry {
  if (lod !== 2) return { w: v.imageId ? 330 : 270, h: PAD + IMG + 2 };
  const w = 390;
  const textW = w - 2 * PAD - (v.imageId ? IMG + 12 : 0);
  const titleLines = wrapText(v.title, textW, TITLE.size, 2, true).length || 1;
  const head = Math.max(PAD + titleLines * TITLE.line + 20, v.imageId ? PAD + IMG : 0);
  const hoverLines = v.hover ? wrapText(v.hover, w - 2 * PAD, HOVER.size, 4).length : 0;
  const h = head + (hoverLines ? 10 + hoverLines * HOVER.line : 0) + 34;
  return { w, h: Math.max(h, 124) };
}

const esc = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * Karte als SVG (als Data-URI an den Cytoscape-Knoten gehängt):
 * Titel, Typname, Titelbild, zwei Farbpunkte (Typfarbe + eigene Farbe) und auf der nahen Stufe der Hover-Text. Der Inhalt des Nodes
 * wird getrennt als Streifen darüber gezeichnet (contentSvg).
 * Das Bild muss als Data-URI vorliegen (SVG-Bilder laden keine externen Dateien).
 */
export function cardSvg(v: VNode, lod: 1 | 2, theme: Theme, imageData: string | null): string {
  const { w, h } = cardGeometry(v, lod);
  const hasImage = !!imageData;
  const textX = PAD + (hasImage ? IMG + 12 : 0);
  const textW = w - textX - PAD;
  const p: string[] = [];
  p.push(`<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">`);
  p.push(`<defs><clipPath id="c"><rect x="${PAD}" y="${PAD}" width="${IMG}" height="${IMG}" rx="10"/></clipPath></defs>`);
  p.push(`<rect x="1" y="1" width="${w - 2}" height="${h - 2}" rx="13" fill="${theme.surface}" stroke="${theme.border}" stroke-width="2"/>`);
  p.push(`<rect x="1" y="1" width="7" height="${h - 2}" rx="3.5" fill="${esc(v.typeColor)}"/>`);
  if (imageData) {
    p.push(`<image x="${PAD}" y="${PAD}" width="${IMG}" height="${IMG}" preserveAspectRatio="xMidYMid slice" clip-path="url(#c)" href="${esc(imageData)}" xlink:href="${esc(imageData)}"/>`);
  }
  let y = PAD + TITLE.size;
  for (const line of wrapText(v.title, textW, TITLE.size, 2, true)) {
    p.push(`<text x="${textX}" y="${y}" font-family="${FONT}" font-size="${TITLE.size}" font-weight="700" fill="${theme.text}">${esc(line)}</text>`);
    y += TITLE.line;
  }
  // Klasse und Schlüssel in einer Zeile (zu lang: gekürzt, damit nichts über die Farbpunkte bzw. den Rand läuft)
  const meta = wrapText(v.refKey ? `${v.typeName} · @${v.refKey}` : v.typeName, textW - 44, META, 1)[0] ?? '';
  p.push(`<text x="${textX}" y="${y + 2}" font-family="${FONT}" font-size="${META}" fill="${theme.muted}">${esc(meta)}</text>`);
  y = Math.max(y + 20, hasImage ? PAD + IMG + 4 : 0);

  if (lod === 2) {
    if (v.hover) {
      y += 10;
      for (const line of wrapText(v.hover, w - 2 * PAD, HOVER.size, 4)) {
        y += HOVER.line;
        p.push(`<text x="${PAD}" y="${y}" font-family="${FONT}" font-size="${HOVER.size}" fill="${theme.text}">${esc(line)}</text>`);
      }
    }
  }
  // zwei Farbpunkte: Typfarbe und eigene Farbe (gestrichelter Ring, wenn keine gewählt)
  const cy = h - 20;
  p.push(`<circle cx="${w - 46}" cy="${cy}" r="8" fill="${esc(v.typeColor)}"/>`);
  if (v.tagColor) p.push(`<circle cx="${w - 24}" cy="${cy}" r="8" fill="${esc(v.tagColor)}"/>`);
  else p.push(`<circle cx="${w - 24}" cy="${cy}" r="7.5" fill="none" stroke="${theme.border}" stroke-width="2" stroke-dasharray="3 2"/>`);
  // Widersprüche (PF 54): roter Punkt oben rechts
  p.push(issueDot(w - 14, 14, v.issues ?? 0, theme, 10));
  p.push('</svg>');
  return p.join('');
}

export const svgUri = (svg: string): string => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

/**
 * Widersprüche (PF 54): roter Punkt mit der Zahl, oben rechts an der Marke bzw. Karte (`r`: Radius; ab zehn „9+“). Ohne Widersprüche
 * nichts.
 */
export function issueDot(cx: number, cy: number, n: number, theme: Theme, r = 7): string {
  if (!(n > 0)) return '';
  const text = n > 9 ? '9+' : String(n);
  const size = r * (text.length > 1 ? 1.05 : 1.35);
  return `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${theme.danger}" stroke="${theme.surface}" stroke-width="1.5"/><text x="${cx}" y="${cy + size * 0.36}" text-anchor="middle" font-family="${FONT}" font-size="${size}" font-weight="800" fill="#ffffff">${text}</text>`;
}
